import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X, RefreshCw, FileSpreadsheet, Layers } from 'lucide-react';
import { vouchersApi, itemsApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';

type Row = {
  item_id: number;
  item_name: string;
  batch_name: string;
  opening_qty: number; opening_amount: number;
  purchase_qty: number; purchase_amount: number;
  sales_qty: number; sales_amount: number;
  credit_note_qty: number; credit_note_amount: number;
  debit_note_qty: number; debit_note_amount: number;
  stock_journal_qty: number; stock_journal_amount: number;
  closing_qty: number; closing_amount: number;
  /** Earliest purchase into this batch, and the last sale out of it. */
  purchase_date: string | null;
  sales_date: string | null;
};
type Totals = {
  opening_qty: number; opening_amount: number;
  purchase_qty: number; purchase_amount: number;
  sales_qty: number; sales_amount: number;
  credit_note_qty: number; credit_note_amount: number;
  debit_note_qty: number; debit_note_amount: number;
  stock_journal_qty: number; stock_journal_amount: number;
  closing_qty: number; closing_amount: number;
  items: number; batches: number;
};
type Txn = {
  vch_id: number; vch_no: string | null; vch_date: string;
  family: string; vch_type: string;
  party_id: number | null; party_name: string | null;
  direction: 'in' | 'out' | 'none';
  qty: number; rate: number; amount: number; balance_qty: number;
};

const ZERO: Totals = {
  stock_journal_qty: 0, stock_journal_amount: 0,
  opening_qty: 0, opening_amount: 0,
  purchase_qty: 0, purchase_amount: 0, sales_qty: 0, sales_amount: 0,
  credit_note_qty: 0, credit_note_amount: 0, debit_note_qty: 0, debit_note_amount: 0,
  closing_qty: 0, closing_amount: 0, items: 0, batches: 0,
};

const fmt = (n: any) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n: any) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
// Zero is noise in a grid this wide — show a dash so the eye finds the real figures.
const num = (n: any, f: (x: any) => string) => (Number(n || 0) === 0 ? '—' : f(n));
const fmtDate = (s: any) => { const d = new Date(String(s)); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };
const toInputDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function BatchSummaryReport() {
  const { showError, showSuccess } = useToast();
  const { canCheckPermission } = useAuth();
  // Viewing the report does not imply being allowed to take it away as a file.
  const canExport = canCheckPermission('reports_batch_summary', 'export');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [itemId, setItemId] = useState<number | ''>('');
  const [groupId, setGroupId] = useState<number | ''>('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [items, setItems] = useState<Array<{ id: number; item_name: string; item_group_id: number | null; category_id: number | null }>>([]);
  const [groups, setGroups] = useState<Array<{ id: number; name: string; parent_id: number | null }>>([]);
  const [categories, setCategories] = useState<Array<{ id: number; name: string; parent_id: number | null }>>([]);
  // Always on and not changeable: batches with nothing left on hand are never
  // shown. The control is kept visible, rather than removed, so it is obvious
  // WHY exhausted batches are missing instead of the report looking incomplete.
  const hideZeroClosing = true;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);

  // Drill-down
  const [drill, setDrill] = useState<Row | null>(null);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [txnLoading, setTxnLoading] = useState(false);

  useEffect(() => {
    itemsApi.getAll()
      .then(r => { if ((r as any)?.success) setItems(((r as any).data || []).filter((i: any) => i?.id)); })
      .catch(() => {});
    itemsApi.getGroups()
      .then(r => { if ((r as any)?.success) setGroups((r as any).data || []); })
      .catch(() => {});
    itemsApi.getCategories()
      .then(r => { if ((r as any)?.success) setCategories((r as any).data || []); })
      .catch(() => {});
  }, []);

  // Both trees are hierarchical, so the picker is indented and a parent
  // selection means "this and everything under it" (matched server-side).
  const buildTree = (nodes: Array<{ id: number; name: string; parent_id: number | null }>) => {
    const ids = new Set(nodes.map(n => n.id));
    const byParent = new Map<number | null, typeof nodes>();
    for (const n of nodes) {
      const k = n.parent_id != null && ids.has(n.parent_id) ? n.parent_id : null;
      if (!byParent.has(k)) byParent.set(k, [] as any);
      byParent.get(k)!.push(n);
    }
    Array.from(byParent.values()).forEach(arr => arr.sort((a, b) => String(a.name).localeCompare(String(b.name))));
    const out: Array<{ id: number; label: string }> = [];
    const walk = (list: typeof nodes, depth: number, seen: Set<number>) => {
      for (const n of list) {
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        out.push({ id: n.id, label: `${'\u00a0\u00a0'.repeat(depth)}${depth ? '\u2514 ' : ''}${n.name}` });
        walk((byParent.get(n.id) || []) as any, depth + 1, seen);
      }
    };
    walk((byParent.get(null) || []) as any, 0, new Set());
    return out;
  };
  const groupOptions = useMemo(() => buildTree(groups), [groups]);
  const categoryOptions = useMemo(() => buildTree(categories), [categories]);

  // Descendant ids for narrowing the Item list to match the chosen tree node.
  const descendantsOf = (nodes: Array<{ id: number; parent_id: number | null }>, root: number) => {
    const kids = new Map<number, number[]>();
    for (const n of nodes) if (n.parent_id != null && n.parent_id !== n.id) {
      kids.set(n.parent_id, [...(kids.get(n.parent_id) || []), n.id]);
    }
    const out = new Set<number>(); const stack = [root];
    while (stack.length) { const id = stack.pop()!; if (out.has(id)) continue; out.add(id); (kids.get(id) || []).forEach(k => stack.push(k)); }
    return out;
  };

  // The Item dropdown lists only items inside the chosen Group / Category, so
  // the three filters can never contradict each other.
  const itemOptions = useMemo(() => {
    const gIds = groupId ? descendantsOf(groups, Number(groupId)) : null;
    const cIds = categoryId ? descendantsOf(categories, Number(categoryId)) : null;
    return items
      .filter(i => (!gIds || (i.item_group_id != null && gIds.has(Number(i.item_group_id))))
                && (!cIds || (i.category_id != null && cIds.has(Number(i.category_id)))))
      .sort((a, b) => String(a.item_name || '').localeCompare(String(b.item_name || '')));
  }, [items, groups, categories, groupId, categoryId]);

  // A stale item selection outside the new Group/Category would silently
  // return nothing, so clear it when it falls out of range.
  useEffect(() => {
    if (itemId && !itemOptions.some(i => i.id === Number(itemId))) setItemId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemOptions]);

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getBatchWiseSummary({
        search: debounced || undefined, item_id: itemId ? Number(itemId) : undefined,
        item_group_id: groupId ? Number(groupId) : undefined,
        category_id: categoryId ? Number(categoryId) : undefined,
      });
      setRows((res.data?.rows || []) as Row[]);
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to load report');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, itemId, groupId, categoryId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDrill = async (r: Row) => {
    setDrill(r); setTxns([]); setTxnLoading(true);
    try {
      const res = await vouchersApi.getBatchTransactions({
        item_id: r.item_id, batch_name: r.batch_name,
      });
      setTxns((res.data?.rows || []) as Txn[]);
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to load batch transactions');
    } finally { setTxnLoading(false); }
  };

  // Esc closes the drill-down.
  useEffect(() => {
    if (!drill) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrill(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drill]);

  // "Hide zero closing stock" filters in the browser so the toggle is instant.
  // Totals are recomputed from what is actually on screen — a footer that still
  // counted hidden rows would not tie out against the visible figures.
  const visible = useMemo(
    () => (hideZeroClosing ? rows.filter(r => Number(r.closing_qty || 0) !== 0) : rows),
    [rows, hideZeroClosing],
  );
  const totals = useMemo<Totals>(() => {
    const t = { ...ZERO };
    for (const r of visible) {
      t.opening_qty += Number(r.opening_qty) || 0;
      t.opening_amount += Number(r.opening_amount) || 0;
      t.purchase_qty += Number(r.purchase_qty) || 0;
      t.purchase_amount += Number(r.purchase_amount) || 0;
      t.sales_qty += Number(r.sales_qty) || 0;
      t.sales_amount += Number(r.sales_amount) || 0;
      t.credit_note_qty += Number(r.credit_note_qty) || 0;
      t.credit_note_amount += Number(r.credit_note_amount) || 0;
      t.debit_note_qty += Number(r.debit_note_qty) || 0;
      t.debit_note_amount += Number(r.debit_note_amount) || 0;
      t.stock_journal_qty += Number(r.stock_journal_qty) || 0;
      t.stock_journal_amount += Number(r.stock_journal_amount) || 0;
      t.closing_qty += Number(r.closing_qty) || 0;
      t.closing_amount += Number(r.closing_amount) || 0;
    }
    t.items = new Set(visible.map(r => r.item_id)).size;
    t.batches = visible.length;
    return t;
  }, [visible]);

  const exportExcel = async () => {
    if (!canExport) {
      showError('Not allowed', 'You do not have permission to export this report');
      return;
    }
    if (visible.length === 0) { showError('Export', 'No data to export'); return; }
    const { utils, writeFile } = await import('xlsx');
    const data: any[] = visible.map((r, i) => ({
      'Sr': i + 1,
      'ItemName': r.item_name,
      'Batch Name': r.batch_name,
      'Opening Qty': r.opening_qty,
      'Opening Amount': r.opening_amount,
      'Purchase Qty': r.purchase_qty,
      'Purchase Amount': r.purchase_amount,
      'Sales Qty': r.sales_qty,
      'Sales Amount': r.sales_amount,
      'Credit Note Qty': r.credit_note_qty,
      'Credit Not Amount': r.credit_note_amount,
      'Debit Note Qty': r.debit_note_qty,
      'Debit Note Amount': r.debit_note_amount,
      'Stock Journal Qty': r.stock_journal_qty,
      'Stock Journal Amount': r.stock_journal_amount,
      'Closing Qty': r.closing_qty,
      'Closing Amount': r.closing_amount,
    }));
    data.push({
      'Sr': '', 'ItemName': 'TOTAL', 'Batch Name': `${totals.batches} batches / ${totals.items} items`,
      'Opening Qty': totals.opening_qty, 'Opening Amount': totals.opening_amount,
      'Purchase Qty': totals.purchase_qty, 'Purchase Amount': totals.purchase_amount,
      'Sales Qty': totals.sales_qty, 'Sales Amount': totals.sales_amount,
      'Credit Note Qty': totals.credit_note_qty, 'Credit Not Amount': totals.credit_note_amount,
      'Debit Note Qty': totals.debit_note_qty, 'Debit Note Amount': totals.debit_note_amount,
      'Stock Journal Qty': totals.stock_journal_qty, 'Stock Journal Amount': totals.stock_journal_amount,
      'Closing Qty': totals.closing_qty, 'Closing Amount': totals.closing_amount,
    });
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Batch Summary');
    writeFile(wb, `Batch_Summary_${toInputDate(new Date())}.xlsx`);
    showSuccess('Exported', 'Excel downloaded');
  };

  const th = 'px-2.5 py-2 text-[11px] font-bold text-gray-700 border border-gray-300 bg-slate-100 whitespace-nowrap';
  const td = 'px-2.5 py-1.5 text-[12.5px] border border-gray-200 whitespace-nowrap';
  const tf = 'px-2.5 py-2 text-[12.5px] font-bold border border-gray-300 bg-slate-50 whitespace-nowrap';
  // Negative closing = sold more than was ever purchased (services, or a batch
  // entered on the sales side only). Worth showing in red rather than hiding.
  const closingTone = (n: number) => (n < 0 ? 'text-red-600' : n > 0 ? 'text-emerald-700' : '');

  return (
    <div className="p-3 sm:p-4">
      {/* Header + filters — single row */}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto">
        <Layers className="h-5 w-5 text-blue-600 shrink-0" />
        <h1 className="text-lg font-bold text-gray-800 whitespace-nowrap shrink-0">Batch-wise Summary</h1>
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <select value={groupId} onChange={e => setGroupId(e.target.value ? Number(e.target.value) : '')}
            title="Filter by item group (includes sub-groups)"
            className="border border-slate-300 rounded px-2 py-1.5 text-[13px] bg-white outline-none focus:ring-1 focus:ring-blue-300 max-w-[170px]">
            <option value="">All Groups</option>
            {groupOptions.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
          <select value={categoryId} onChange={e => setCategoryId(e.target.value ? Number(e.target.value) : '')}
            title="Filter by category (includes sub-categories)"
            className="border border-slate-300 rounded px-2 py-1.5 text-[13px] bg-white outline-none focus:ring-1 focus:ring-blue-300 max-w-[170px]">
            <option value="">All Categories</option>
            {categoryOptions.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select value={itemId} onChange={e => setItemId(e.target.value ? Number(e.target.value) : '')}
            title="Filter by item"
            className="border border-slate-300 rounded px-2 py-1.5 text-[13px] bg-white outline-none focus:ring-1 focus:ring-blue-300 max-w-[220px]">
            <option value="">{groupId || categoryId ? `All Items (${itemOptions.length})` : 'All Items'}</option>
            {itemOptions.map(i => <option key={i.id} value={i.id}>{i.item_name}</option>)}
          </select>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search item / batch…"
              className="pl-8 pr-8 py-1.5 border border-slate-300 rounded text-[13px] w-44 outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={13} /></button>}
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 rounded text-[13px] bg-slate-50 text-slate-500 select-none"
            title="Always on: this report only shows batches that still have stock on hand">
            <input type="checkbox" className="accent-blue-600" checked readOnly disabled />
            Hide zero closing
          </span>
          <button onClick={fetchData} className="p-2 rounded text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></button>
          {canExport && (
            <button onClick={exportExcel} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-300 rounded text-[13px] text-green-700 bg-green-50 hover:bg-green-100"><FileSpreadsheet size={14} /> Excel</button>
          )}
        </div>
      </div>

      <div className="overflow-auto border border-gray-300 rounded bg-white" style={{ maxHeight: 'calc(100vh - 180px)' }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={`${th} w-[46px] text-center`} rowSpan={2}>Sr</th>
              <th className={`${th} text-left`} rowSpan={2}>ItemName</th>
              <th className={`${th} text-left`} rowSpan={2}>Batch Name</th>
              <th className={`${th} text-center`} rowSpan={2}>Purchase Date</th>
              <th className={`${th} text-center`} rowSpan={2}>Sales Date</th>
              <th className={`${th} text-center`} colSpan={2}>Opening</th>
              <th className={`${th} text-center`} colSpan={2}>Purchase</th>
              <th className={`${th} text-center`} colSpan={2}>Sales</th>
              <th className={`${th} text-center`} colSpan={2}>Credit Note</th>
              <th className={`${th} text-center`} colSpan={2}>Debit Note</th>
              <th className={`${th} text-center`} colSpan={2} title="Net of destination (in) and source (out) movements">Stock Journal</th>
              <th className={`${th} text-center`} colSpan={2}>Closing</th>
            </tr>
            <tr>
              <th className={`${th} text-right`}>Qty</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={`${th} text-right`}>Qty</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={`${th} text-right`}>Qty</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={`${th} text-right`}>Qty</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={`${th} text-right`}>Qty</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={`${th} text-right`}>Qty</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={`${th} text-right`}>Qty</th>
              <th className={`${th} text-right`}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={19} className="py-12 text-center text-gray-400 text-sm">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-40" />Loading…
              </td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={19} className="py-12 text-center text-gray-400 text-sm italic">
                {rows.length > 0 && hideZeroClosing ? 'No batch has closing stock in this period' : 'No batch movement in this period'}
              </td></tr>
            ) : visible.map((r, i) => (
              <tr key={`${r.item_id}|${r.batch_name}`} onClick={() => openDrill(r)}
                title="Click to see this batch's transactions"
                className="hover:bg-blue-50/60 cursor-pointer">
                <td className={`${td} text-center text-gray-400`}>{i + 1}</td>
                <td className={`${td} font-semibold text-gray-900`} title={r.item_name}>{r.item_name}</td>
                <td className={`${td} text-blue-700`} title={r.batch_name}>{r.batch_name}</td>
                <td className={`${td} text-center whitespace-nowrap text-slate-600`}>{r.purchase_date ? fmtDate(r.purchase_date) : '—'}</td>
                <td className={`${td} text-center whitespace-nowrap text-slate-600`}>{r.sales_date ? fmtDate(r.sales_date) : '—'}</td>
                <td className={`${td} text-right tabular-nums text-slate-600`}>{num(r.opening_qty, fmtQty)}</td>
                <td className={`${td} text-right tabular-nums text-slate-600`}>{num(r.opening_amount, fmt)}</td>
                <td className={`${td} text-right tabular-nums`}>{num(r.purchase_qty, fmtQty)}</td>
                <td className={`${td} text-right tabular-nums`}>{num(r.purchase_amount, fmt)}</td>
                <td className={`${td} text-right tabular-nums`}>{num(r.sales_qty, fmtQty)}</td>
                <td className={`${td} text-right tabular-nums`}>{num(r.sales_amount, fmt)}</td>
                <td className={`${td} text-right tabular-nums text-red-600`}>{num(r.credit_note_qty, fmtQty)}</td>
                <td className={`${td} text-right tabular-nums text-red-600`}>{num(r.credit_note_amount, fmt)}</td>
                <td className={`${td} text-right tabular-nums text-amber-700`}>{num(r.debit_note_qty, fmtQty)}</td>
                <td className={`${td} text-right tabular-nums text-amber-700`}>{num(r.debit_note_amount, fmt)}</td>
                <td className={`${td} text-right tabular-nums ${closingTone(Number(r.stock_journal_qty))}`}>{num(r.stock_journal_qty, fmtQty)}</td>
                <td className={`${td} text-right tabular-nums ${closingTone(Number(r.stock_journal_qty))}`}>{num(r.stock_journal_amount, fmt)}</td>
                <td className={`${td} text-right tabular-nums font-semibold ${closingTone(Number(r.closing_qty))}`}>{num(r.closing_qty, fmtQty)}</td>
                <td className={`${td} text-right tabular-nums font-semibold ${closingTone(Number(r.closing_qty))}`}>{num(r.closing_amount, fmt)}</td>
              </tr>
            ))}
          </tbody>
          {visible.length > 0 && (
            <tfoot className="sticky bottom-0">
              <tr>
                <td className={`${tf} text-center`} />
                <td className={`${tf} text-left`}>TOTAL</td>
                <td className={`${tf} text-left text-gray-500 font-normal`}>{totals.batches} batches · {totals.items} items</td>
                <td className={`${tf} text-right tabular-nums text-slate-600`}>{fmtQty(totals.opening_qty)}</td>
                <td className={`${tf} text-right tabular-nums text-slate-600`}>{fmt(totals.opening_amount)}</td>
                <td className={`${tf} text-right tabular-nums`}>{fmtQty(totals.purchase_qty)}</td>
                <td className={`${tf} text-right tabular-nums`}>{fmt(totals.purchase_amount)}</td>
                <td className={`${tf} text-right tabular-nums`}>{fmtQty(totals.sales_qty)}</td>
                <td className={`${tf} text-right tabular-nums`}>{fmt(totals.sales_amount)}</td>
                <td className={`${tf} text-right tabular-nums text-red-700`}>{fmtQty(totals.credit_note_qty)}</td>
                <td className={`${tf} text-right tabular-nums text-red-700`}>{fmt(totals.credit_note_amount)}</td>
                <td className={`${tf} text-right tabular-nums text-amber-700`}>{fmtQty(totals.debit_note_qty)}</td>
                <td className={`${tf} text-right tabular-nums text-amber-700`}>{fmt(totals.debit_note_amount)}</td>
                <td className={`${tf} text-right tabular-nums ${closingTone(totals.stock_journal_qty)}`}>{fmtQty(totals.stock_journal_qty)}</td>
                <td className={`${tf} text-right tabular-nums ${closingTone(totals.stock_journal_qty)}`}>{fmt(totals.stock_journal_amount)}</td>
                <td className={`${tf} text-right tabular-nums ${closingTone(totals.closing_qty)}`}>{fmtQty(totals.closing_qty)}</td>
                <td className={`${tf} text-right tabular-nums ${closingTone(totals.closing_qty)}`}>{fmt(totals.closing_amount)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Batch transactions */}
      {drill && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setDrill(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
              <div>
                <h2 className="text-[15px] font-bold text-gray-900">{drill.item_name}</h2>
                <p className="text-[12.5px] text-gray-500">
                  Batch <span className="font-semibold text-blue-700">{drill.batch_name}</span>
                  <span className="mx-2 text-gray-300">|</span>
                  Closing <span className={`font-semibold ${closingTone(Number(drill.closing_qty))}`}>{fmtQty(drill.closing_qty)}</span>
                  <span className="mx-2 text-gray-300">|</span>
                  full history
                </p>
              </div>
              <button onClick={() => setDrill(null)} className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Close (Esc)"><X size={18} /></button>
            </div>

            <div className="overflow-auto flex-1">
              {txnLoading ? (
                <div className="py-14 text-center text-gray-400 text-sm">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-40" />Loading transactions…
                </div>
              ) : txns.length === 0 ? (
                <div className="py-14 text-center text-gray-400 text-sm italic">No transactions for this batch</div>
              ) : (
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className={`${th} text-left`}>Date</th>
                      <th className={`${th} text-left`}>Voucher No</th>
                      <th className={`${th} text-left`}>Type</th>
                      <th className={`${th} text-left`}>Party</th>
                      <th className={`${th} text-right`}>In</th>
                      <th className={`${th} text-right`}>Out</th>
                      <th className={`${th} text-right`}>Rate</th>
                      <th className={`${th} text-right`}>Amount</th>
                      <th className={`${th} text-right`}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t, i) => (
                      <tr key={`${t.vch_id}-${i}`} className="hover:bg-slate-50">
                        <td className={`${td} text-gray-600`}>{fmtDate(t.vch_date)}</td>
                        <td className={`${td} font-semibold text-gray-900`}>{t.vch_no || '—'}</td>
                        <td className={`${td}`}>
                          <span className={`px-1.5 py-0.5 rounded text-[10.5px] font-bold uppercase ${
                            t.direction === 'in' ? 'bg-emerald-100 text-emerald-700'
                            : t.direction === 'out' ? 'bg-red-100 text-red-700'
                            : 'bg-slate-100 text-slate-600'}`}>{t.vch_type}</span>
                        </td>
                        <td className={`${td} text-gray-700 max-w-[220px] truncate`} title={t.party_name || ''}>{t.party_name || '—'}</td>
                        <td className={`${td} text-right tabular-nums text-emerald-700`}>{t.direction === 'in' ? fmtQty(t.qty) : '—'}</td>
                        <td className={`${td} text-right tabular-nums text-red-600`}>{t.direction === 'out' ? fmtQty(t.qty) : '—'}</td>
                        <td className={`${td} text-right tabular-nums text-gray-500`}>{num(t.rate, fmt)}</td>
                        <td className={`${td} text-right tabular-nums`}>{num(t.amount, fmt)}</td>
                        <td className={`${td} text-right tabular-nums font-semibold ${closingTone(Number(t.balance_qty))}`}>{fmtQty(t.balance_qty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="px-4 py-2 border-t bg-slate-50 text-[12px] text-gray-500">
              {txns.length} transaction{txns.length === 1 ? '' : 's'} · newest first · balance runs oldest → newest
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
